import i18n from "@/plugins/i18n";
import siret from "siret";
import { phoneNumberRegexes } from "@/utils/constants";
import { formatPhone } from "@/utils/misc";
import VueI18n from "vue-i18n";

export const validator = require("validator");
export const optionsEmail = {
  allow_utf8_local_part: false, // Disallow UTF-8 characters in the local part
  blacklisted_chars: "!#$%&'*+/=?^_`{|}~", // Exclude these special characters
};

export const regex = {
  uppercase: /^(?=.*[A-Z])/,
  lowercase: /^(?=.*[a-z])/,
  passwordLength: /^(?=.{8,})/,
  number: /^(?=.*[0-9])/,
  special: /^(?=.*?[^\w\s])/,
  validPhoneNumber: RegExp(Object.values(phoneNumberRegexes).map((r: any) => r.source).join("|")),
  token: /^[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}$/,
  masterlockQrCode: /^[A-Z0-9]{6}:[A-Z0-9]{8}$/,
  imei: /^\d{15}$/,
  zipCode: /^\d{5}$/,
  latitude: /^(-?([1-8]?\d(\.\d+)?|90(\.0+)?))$/,
  longitude: /^(-?(1[0-7]\d|0?\d{1,2})(\.\d+)?|180(\.0+)?)$/,
  registrationNumber: /^[A-Z]{2}\d{7}$/,
};

export const formRules = {
  required: (v: string) => {
    return !!v || i18n.t("rules.required");
  },
  requiredNumber: (v: number) => {
    return !isNaN(v) || i18n.t("rules.requiredNumber");
  },
  email: (v: string) => {
    return v ? validator.isEmail(v, optionsEmail) || i18n.t("rules.formatEmail") : true;
  },
  isEmail: (v: string) => {
    return v ? validator.isEmail(v, optionsEmail) : false;
  },
  phoneOrEmail : (v: string) => {
    return validator.isEmail(v, optionsEmail)
      || regex.validPhoneNumber.test(formatPhone(v))
      || i18n.t("rules.formatEmailOrPhone");
  },
  mainContact: (v: string) => {
    return validator.isEmail(v, optionsEmail) || regex.validPhoneNumber.test(v)
     || i18n.t("rules.validEmailOrPhone");
  },
  uppercase: (v: string) => {
    return regex.uppercase.test(v) || i18n.t("rules.password.uppercase");
  },
  lowercase: (v: string) => {
    return regex.lowercase.test(v) || i18n.t("rules.password.lowercase");
  },
  length: (v: string) => {
    return regex.passwordLength.test(v) || i18n.t("rules.password.length");
  },
  number: (v: string) => {
    return regex.number.test(v) || i18n.t("rules.password.number");
  },
  special: (v: string) => {
    return regex.special.test(v) || i18n.t("rules.password.special");
  },
  terms: (v: string) => {
    return !!v || i18n.t("rules.terms");
  },
  phone: (v: string) => {
    return v ? regex.validPhoneNumber.test(v) || i18n.t("rules.phone") : true;
  },
  isPhone: (v: string): boolean => {
    return v ? regex.validPhoneNumber.test(v) : false;
  },
  avatar: (v: {size: number}) => {
    const AVATAR_MAX_SIZE = 2000000; // in bytes
    return v.size < AVATAR_MAX_SIZE || i18n.t("rules.avatarSize", {maxSize: AVATAR_MAX_SIZE / 1000});
  },
  siret: (v: string, errorReturnValue: VueI18n.TranslateResult | boolean = i18n.t("rules.siret")) => {
    return !v || siret.isSIRET(v) || errorReturnValue;
  },
  siren: (v: string) => {
    return !v || siret.isSIREN(v) || i18n.t("rules.siren");
  },
  positive: (v: string) => {
    return !v || Number(v) >= 0 || i18n.t("rules.positive");
  },
  strictlyPositive: (v: string) => {
    return !v || Number(v) > 0 || i18n.t("rules.strictlyPositive");
  },
  percent: (v: string) => {
    return !v || (Number(v) >= 0 && Number(v) <= 100) || i18n.t("rules.percent");
  },
  integer: (v: string) => {
    return !v || Number.isInteger(Number(v)) || i18n.t("rules.integer");
  },
  multiplier: (v: string) => {
    return !v || (Number(v) >= 0) || i18n.t("rules.multiplier");
  },
  pin: (v: string) => {
    return !v || v.length === 4 || "";
  },
  zipCode: (v: string) => {
    return !v || regex.zipCode.test(v) || i18n.t("rules.zipCode");
  },
  latitude: (v: string) => {
    return !v || regex.latitude.test(v) || i18n.t("rules.latitude");
  },
  longitude: (v: string) => {
    return !v || regex.longitude.test(v) || i18n.t("rules.longitude");
  },
  registrationNumber: (v: string) => {
    return !v || regex.registrationNumber.test(v) || i18n.t("rules.registrationNumber");
  },
};
